import { Box, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import ApiClient from '../../backend/ApiClient'
import LocalCache from '../../backend/LocalCache'
import { useAppDispatch } from '../../store/Hooks'
import { setCurrentUser } from '../../store/slices/AppSlice'
import { changeRoute, setAllUserRoles, setJWT } from '../../store/slices/SessionSlice'
import HelixLoader from '../loaders/HelixLoader'
import RoutingManager from '../routing/Routing'
import AppInitializer from './AppInitializer'

export default function AuthenticatorInitializer() {
    const dispatch = useAppDispatch()
    const [isAuthenticated, setIsAuthenticated] = useState(false)
    const [loading, setLoading] = useState(true)
    const [error, setSerror] = useState<Error>()

    useEffect(() => {
        (async () => {
            try {
                type RouteContainer = {
                    pathName: string
                    parameters?: { name: string, value: any }[]
                }

                let routeContainer: RouteContainer = {
                    pathName: LocalCache.get('lastRoute')?.value || window.location.pathname,
                    parameters: []
                }

                const paramsFromUrl = new URLSearchParams(window.location.search)

                paramsFromUrl?.forEach((value, key) => {
                    routeContainer.parameters.push({ name: key, value })
                })

                const hash = window.location.hash.substring(1)
                const params = new URLSearchParams(hash)
                const idTokenFromUrl = params.get('id_token')
                const accessTokenFromUrl = params.get('access_token')

                let idToken: string = null
                let accessToken: string = null

                if (idTokenFromUrl) {
                    let cognitoState = params.get('state')

                    if (cognitoState) {
                        cognitoState = atob(cognitoState)
                        routeContainer = JSON.parse(cognitoState) as RouteContainer
                    }

                    idToken = idTokenFromUrl
                    accessToken = accessTokenFromUrl

                    LocalCache.set('id_token', idTokenFromUrl)
                    LocalCache.set('access_token', accessTokenFromUrl)

                    window.history.replaceState(null, '', window.location.pathname)
                } else {
                    idToken = LocalCache.get('id_token')?.value || null
                    accessToken = LocalCache.get('access_token')?.value || null
                }

                if (!idToken) {
                    const cognitoURL = await ApiClient.Auth.getCognitoRedirectURL(window.location.origin) + `&state=${btoa(JSON.stringify(routeContainer))}`

                    window.location.href = cognitoURL
                    return
                }

                const isAuthorized = await ApiClient.Auth.checkAuthorization(idToken, accessToken, res => {
                    if (res.status === 401 || res.status === 403) {
                        LocalCache.clearAll()
                        window.location.href = window.location.origin
                    } else {
                        throw new Error(`Authorization check failed with status ${res.status}`)
                    }
                })

                if (!isAuthorized) {
                    LocalCache.clearAll()
                    window.location.href = window.location.origin
                    return
                }

                dispatch(setJWT(idToken))
                const userData = await ApiClient.Auth.getLoginUserData(idToken)
                dispatch(setCurrentUser(userData))

                const strictRoute = RoutingManager.getRouteFromLocationStrict(routeContainer.pathName)

                const paramsObject = {}
                routeContainer?.parameters.forEach(param => {
                    paramsObject[param.name] = param.value
                })

                dispatch(changeRoute({ route: strictRoute, params: paramsObject }))

                const allRoles = await ApiClient.getAllUserRoles()
                dispatch(setAllUserRoles(allRoles))

                setIsAuthenticated(true)
                setLoading(false)
            } catch (error) {
                setSerror(error)
            }
        })()
    }, [])

    if (error) throw error
    if (loading) return (
        <Box display='flex' flex={1} height='100vh' width='100vw' justifyContent='center' alignItems='center'>
            <HelixLoader size={64} />
        </Box>
    )
    if (!isAuthenticated) return (
        <Box display='flex' flex={1} height='100vh' width='100vw' justifyContent='center' alignItems='center'>
            <Typography>Please log in to access the application.</Typography>
        </Box>
    )
    return <AppInitializer />
}
